"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = useCommunityEnabled;
/**
 * Shared gate for **community profile** surfaces (profiles, playlists in profile UX,
 * my library shell, comment profile links, header menu avatars, etc.).
 *
 * Two layers of gating:
 *
 *   1. OpenShift / config flag (`features.communityProfile`)
 *      - Set per environment via FEATURES_COMMUNITY_PROFILE env var
 *      - If OFF (0) the feature is completely disabled, full stop
 *
 *   2. PostHog feature flag (`yon-lauren-initial-test`)
 *      - Only evaluated when PostHog itself is enabled (`features.posthog`)
 *      - The user must be in the 'email-matched' variant to see community profile UX
 *      - In local dev / staging where PostHog is OFF, we skip this check
 *        and treat the feature as enabled (so devs can work on it)
 *
 * **Not the same as Bettermode `/community`:** that iframe uses
 * `useCommunityEnabledForNav` in `CommunityPage.jsx`, which reads `features.community`
 * and `computeCommunityEnabled` (`internal-employee` variant or enabled
 * `yon-lauren-initial-test` via `useFeatureFlagEnabled`). Nav and profile UX can
 * therefore disagree by design until flags are aligned.
 *
 * Returns:
 *   enabled  — true when the current user should see community profile features
 *   loading  — true while PostHog is resolving the flag (brief flash)
 */
var react_1 = require("@posthog/react");
var PostHogProvider_1 = require("components/OracleCommons/PostHogProvider");
var config_1 = require("config");
function useCommunityEnabled() {
    var _a, _b;
    var config = (0, config_1.get)();
    var configEnabled = !!((_a = config === null || config === void 0 ? void 0 : config.features) === null || _a === void 0 ? void 0 : _a.communityProfile);
    var isPostHogEnabled = !!((_b = config === null || config === void 0 ? void 0 : config.features) === null || _b === void 0 ? void 0 : _b.posthog);
    var yonLaurenVariant = (0, react_1.useFeatureFlagVariantKey)(PostHogProvider_1.POSTHOG_FEATURE_FLAGS.COMMUNITY_YON_LAUREN_INITIAL_TEST);
    // Determine final enabled state
    var enabled = false;
    if (configEnabled) {
        if (!isPostHogEnabled) {
            // PostHog is OFF (local dev / staging) — config flag alone is enough
            enabled = true;
        }
        else {
            // PostHog is ON (production) — user must be in the right cohort
            enabled = yonLaurenVariant === 'email-matched';
        }
    }
    // While PostHog is resolving the variant is undefined — surface that
    // so consumers can show a loading state instead of a flash of 404
    var loading = !!(configEnabled && isPostHogEnabled && yonLaurenVariant === undefined);
    return { enabled: enabled, loading: loading };
}
